/**
 * Shared specialization configuration.
 *
 * This module defines all available job search specializations
 * with their IDs, names, and default keywords.
 *
 * Used by both API and Bot packages.
 */
/**
 * Specialization IDs for type safety and consistency.
 * The current catalog is still inside the pre-live-user reset window, so IDs
 * follow the approved product order from the 38-role taxonomy.
 */
export const SpecializationId = {
    BACKEND: 0,
    FRONTEND: 1,
    FULLSTACK: 2,
    ANDROID: 3,
    IOS: 4,
    ONE_C: 5,
    DATA_SCIENTIST: 6,
    ML_ENGINEER: 7,
    DATA_ENGINEER: 8,
    AI_ENGINEER: 9,
    SYSTEM_ANALYST: 10,
    BUSINESS_ANALYST: 11,
    DATA_ANALYST: 12,
    QA: 13,
    PRODUCT_DESIGNER: 14,
    UX_UI_DESIGNER: 15,
    GRAPHIC_DESIGNER: 16,
    WEB_DESIGNER: 17,
    MOTION_DESIGNER: 18,
    THREE_D_ARTIST: 19,
    PROJECT_MANAGER: 20,
    PRODUCT_MANAGER: 21,
    DELIVERY_MANAGER: 22,
    DEVOPS_SRE: 23,
    SYSTEM_ADMINISTRATOR: 24,
    INFOSEC_SPECIALIST: 25,
    PENTESTER: 26,
    MARKETER: 27,
    SEO_SPECIALIST: 28,
    SMM_SPECIALIST: 29,
    TARGETOLOGIST: 30,
    PR_SPECIALIST: 31,
    TECHNICAL_WRITER: 32,
    COPYWRITER: 33,
    CONTENT_MANAGER: 34,
    UGC_CREATOR: 35,
    HR_MANAGER: 36,
    RECRUITER: 37,
};
/**
 * Canonical specialization name identifiers, shared by every module that
 * needs to refer to a specialization by name (lexicon, job alerts,
 * scoring). Kept in sync with `SPECIALIZATIONS[].name` by a test in
 * `specializations.test.ts`.
 */
export const SPECIALIZATION_NAMES = [
    "backend",
    "frontend",
    "fullstack",
    "android",
    "ios",
    "one-c",
    "data-scientist",
    "ml-engineer",
    "data-engineer",
    "ai-engineer",
    "system-analyst",
    "business-analyst",
    "data-analyst",
    "qa",
    "product-designer",
    "ux-ui-designer",
    "graphic-designer",
    "web-designer",
    "motion-designer",
    "three-d-artist",
    "project-manager",
    "product-manager",
    "delivery-manager",
    "devops-sre",
    "system-administrator",
    "infosec-specialist",
    "pentester",
    "marketer",
    "seo-specialist",
    "smm-specialist",
    "targetologist",
    "pr-specialist",
    "technical-writer",
    "copywriter",
    "content-manager",
    "ugc-creator",
    "hr-manager",
    "recruiter",
];
/** Russian profile titles shown in the Mini App and Telegram vacancy cards. */
const SPECIALIZATION_TITLES_RU = {
    backend: "Бэкенд разработчик",
    frontend: "Фронтенд разработчик",
    fullstack: "Фулстек разработчик",
    android: "Android разработчик",
    ios: "iOS разработчик",
    "one-c": "1С разработчик",
    "data-scientist": "Data Scientist",
    "ml-engineer": "ML-инженер",
    "data-engineer": "Дата-инженер",
    "ai-engineer": "AI-инженер",
    "system-analyst": "Системный аналитик",
    "business-analyst": "Бизнес-аналитик",
    "data-analyst": "Дата-аналитик",
    qa: "Инженер по тестированию",
    "product-designer": "Продуктовый дизайнер",
    "ux-ui-designer": "UX/UI дизайнер",
    "graphic-designer": "Графический дизайнер",
    "web-designer": "Веб-дизайнер",
    "motion-designer": "Моушн-дизайнер",
    "three-d-artist": "3D-художник",
    "project-manager": "Проджект-менеджер",
    "product-manager": "Продакт-менеджер",
    "delivery-manager": "Деливери-менеджер",
    "devops-sre": "DevOps / SRE инженер",
    "system-administrator": "Системный администратор",
    "infosec-specialist": "Специалист по ИБ",
    pentester: "Пентестер",
    marketer: "Маркетолог",
    "seo-specialist": "SEO-специалист",
    "smm-specialist": "SMM-специалист",
    targetologist: "Таргетолог",
    "pr-specialist": "PR-специалист",
    "technical-writer": "Технический писатель",
    copywriter: "Копирайтер",
    "content-manager": "Контент-менеджер",
    "ugc-creator": "UGC-креатор",
    "hr-manager": "HR-менеджер",
    recruiter: "Рекрутер",
};
/**
 * All available specializations. Order matters for UI display.
 */
export const SPECIALIZATIONS = [
    { id: SpecializationId.BACKEND, name: "backend", displayName: "Backend" },
    { id: SpecializationId.FRONTEND, name: "frontend", displayName: "Frontend" },
    { id: SpecializationId.FULLSTACK, name: "fullstack", displayName: "Fullstack" },
    { id: SpecializationId.ANDROID, name: "android", displayName: "Android" },
    { id: SpecializationId.IOS, name: "ios", displayName: "iOS" },
    { id: SpecializationId.ONE_C, name: "one-c", displayName: "1C" },
    { id: SpecializationId.DATA_SCIENTIST, name: "data-scientist", displayName: "Data Scientist" },
    { id: SpecializationId.ML_ENGINEER, name: "ml-engineer", displayName: "ML Engineer" },
    { id: SpecializationId.DATA_ENGINEER, name: "data-engineer", displayName: "Data Engineer" },
    { id: SpecializationId.AI_ENGINEER, name: "ai-engineer", displayName: "AI Engineer" },
    { id: SpecializationId.SYSTEM_ANALYST, name: "system-analyst", displayName: "System Analyst" },
    { id: SpecializationId.BUSINESS_ANALYST, name: "business-analyst", displayName: "Business Analyst" },
    { id: SpecializationId.DATA_ANALYST, name: "data-analyst", displayName: "Data Analyst" },
    { id: SpecializationId.QA, name: "qa", displayName: "QA" },
    { id: SpecializationId.PRODUCT_DESIGNER, name: "product-designer", displayName: "Product Designer" },
    { id: SpecializationId.UX_UI_DESIGNER, name: "ux-ui-designer", displayName: "UX/UI Designer" },
    { id: SpecializationId.GRAPHIC_DESIGNER, name: "graphic-designer", displayName: "Graphic Designer" },
    { id: SpecializationId.WEB_DESIGNER, name: "web-designer", displayName: "Web Designer" },
    { id: SpecializationId.MOTION_DESIGNER, name: "motion-designer", displayName: "Motion Designer" },
    { id: SpecializationId.THREE_D_ARTIST, name: "three-d-artist", displayName: "3D Artist" },
    { id: SpecializationId.PROJECT_MANAGER, name: "project-manager", displayName: "Project Manager" },
    { id: SpecializationId.PRODUCT_MANAGER, name: "product-manager", displayName: "Product Manager" },
    { id: SpecializationId.DELIVERY_MANAGER, name: "delivery-manager", displayName: "Delivery Manager" },
    { id: SpecializationId.DEVOPS_SRE, name: "devops-sre", displayName: "DevOps/SRE" },
    {
        id: SpecializationId.SYSTEM_ADMINISTRATOR,
        name: "system-administrator",
        displayName: "System Administrator",
    },
    {
        id: SpecializationId.INFOSEC_SPECIALIST,
        name: "infosec-specialist",
        displayName: "InfoSec Specialist",
    },
    { id: SpecializationId.PENTESTER, name: "pentester", displayName: "Pentester" },
    { id: SpecializationId.MARKETER, name: "marketer", displayName: "Marketer" },
    { id: SpecializationId.SEO_SPECIALIST, name: "seo-specialist", displayName: "SEO Specialist" },
    { id: SpecializationId.SMM_SPECIALIST, name: "smm-specialist", displayName: "SMM Specialist" },
    { id: SpecializationId.TARGETOLOGIST, name: "targetologist", displayName: "Targetologist" },
    { id: SpecializationId.PR_SPECIALIST, name: "pr-specialist", displayName: "PR Specialist" },
    { id: SpecializationId.TECHNICAL_WRITER, name: "technical-writer", displayName: "Technical Writer" },
    { id: SpecializationId.COPYWRITER, name: "copywriter", displayName: "Copywriter" },
    { id: SpecializationId.CONTENT_MANAGER, name: "content-manager", displayName: "Content Manager" },
    { id: SpecializationId.UGC_CREATOR, name: "ugc-creator", displayName: "UGC Creator" },
    { id: SpecializationId.HR_MANAGER, name: "hr-manager", displayName: "HR Manager" },
    { id: SpecializationId.RECRUITER, name: "recruiter", displayName: "Recruiter" },
];
/**
 * Gets specialization config by ID.
 * @param id Specialization ID to look up.
 * @returns Specialization config or undefined if not found.
 */
export function getSpecializationById(id) {
    return SPECIALIZATIONS.find((d) => d.id === id);
}
/**
 * Gets specialization config by name.
 * @param name Specialization name to look up (case-insensitive).
 * @returns Specialization config or undefined if not found.
 */
export function getSpecializationByName(name) {
    const normalized = name.toLowerCase().trim();
    return SPECIALIZATIONS.find((d) => d.name === normalized);
}
/** Returns the same Russian title used for a search-profile card in the Mini App. */
export function getSpecializationTitle(nameOrDisplay) {
    const normalized = nameOrDisplay.toLowerCase().trim();
    const specialization = SPECIALIZATIONS.find((item) => item.name === normalized || item.displayName.toLowerCase() === normalized);
    return specialization ? SPECIALIZATION_TITLES_RU[specialization.name] : nameOrDisplay;
}
